import React, { useState } from 'react';

export type PWAInstallButtonVariant = 'inline' | 'floating' | 'navbar';

interface PWAInstallButtonProps {
  /** floating: fixed corner. inline: parent flow. navbar: compact next to logo on dark bar. */
  variant?: PWAInstallButtonVariant;
}

/** Chromium PWA install prompt (not in all TypeScript DOM lib versions). */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type DeferredInstallWindow = Window & { deferredInstallPrompt?: InstallPromptEvent };

/**
 * Captures beforeinstallprompt, shows install UI, and calls prompt() on user action.
 * preventDefault() must be paired with a user-triggered .prompt() — this component does that.
 */
const PWAInstallButton: React.FC<PWAInstallButtonProps> = ({ variant = 'inline' }) => {
  const [showInstall, setShowInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  React.useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    const w = window as DeferredInstallWindow;
    if (w.deferredInstallPrompt) {
      setShowInstall(true);
    }

    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const ev = e as InstallPromptEvent;
      w.deferredInstallPrompt = ev;
      setShowInstall(true);
    };

    const onAppInstalled = () => {
      setIsInstalled(true);
      setShowInstall(false);
      w.deferredInstallPrompt = undefined;
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    const w = window as DeferredInstallWindow;
    const promptEvent = w.deferredInstallPrompt;
    if (!promptEvent) return;

    await promptEvent.prompt();
    await promptEvent.userChoice;
    w.deferredInstallPrompt = undefined;
    setShowInstall(false);
  };

  if (isInstalled || !showInstall) {
    return null;
  }

  const variantClass =
    variant === 'floating'
      ? 'pwa-install-btn--floating'
      : variant === 'navbar'
        ? 'pwa-install-btn--navbar'
        : 'pwa-install-btn--inline';

  return (
    <button
      type="button"
      onClick={handleInstall}
      className={`pwa-install-btn ${variantClass}`}
      aria-label="Instalează aplicația"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 16 12 12 9 9" />
      </svg>
      <span>Instalează aplicația</span>
    </button>
  );
};

export default PWAInstallButton;
